﻿package org.example.project.enginechat

import androidx.compose.runtime.Composable
import org.example.project.service.LoginService
import org.example.project.utils.All
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

data class ChatContactsData(
    val companyLabel: String = "",
    val internalContacts: List<All> = emptyList(),
    val contactCompanies: List<All> = emptyList(),
    val allUsers: Map<String, All> = emptyMap()
)

fun isInternalKontakt(currentFirmenId: String, user: All): Boolean =
    currentFirmenId.isNotBlank() && currentFirmenId == user.firmenId.trim()

fun canSeeKontaktProfil(currentFirmenId: String, user: All): Boolean =
    user.kontakteAktiv && if (isInternalKontakt(currentFirmenId, user)) user.profilIntern else user.profilOnline

fun canSeeKontaktKalender(currentFirmenId: String, user: All): Boolean =
    user.kontakteAktiv && if (isInternalKontakt(currentFirmenId, user)) user.terminkalenderIntern else user.terminkalenderOnline

fun userChatId(user: All): String = user.id

private fun canUseChat(user: All): Boolean {
    return canSeeKontaktProfil(LoginService.getUser()?.firmenId.orEmpty().trim(), user)
}

private fun All?.orEmptyRole(): All = this ?: All()

fun chatIdentityUserId(value: String): String =
    value.trim().substringAfterLast("_", value.trim()).trimStart('_')

fun isCurrentChatUser(user: All, currentFirmenId: String, currentUserId: String, currentChatUserId: String): Boolean {
    val rawId = user.id.trim()
    val rawUserId = chatIdentityUserId(rawId)
    val userFirmenId = user.firmenId.trim()
    return rawId == currentUserId ||
            rawId == currentChatUserId ||
            (userFirmenId == currentFirmenId && rawUserId == currentUserId)
}

fun displayNameById(chatUserId: String, allUsers: Map<String, All>): String {
    val user = allUsers[chatUserId] ?: return chatUserId
    return userDisplayName(user)
}

fun shortDisplayNameById(chatUserId: String, allUsers: Map<String, All>): String {
    val user = allUsers[chatUserId] ?: return chatUserId
    return shortUserDisplayName(user)
}

fun userPositionLabel(user: All): String =
    user.position.ifBlank { user.abteilung }.ifBlank { "-" }

fun userCompanyLabel(user: All): String =
    user.firma.ifBlank { user.kunde }.ifBlank { user.firmenId }.ifBlank { "-" }

fun chatUserPrimaryLine(user: All): String =
    "${userDisplayName(user)} | ${userPositionLabel(user)}"

fun chatUserShortPrimaryLine(user: All): String =
    shortUserDisplayName(user)

fun chatUserHoverLine(user: All): String =
    "${userDisplayName(user)} | ${userPositionLabel(user)} | ${userCompanyLabel(user)}"

fun chatUserPrimaryLineById(chatUserId: String, allUsers: Map<String, All>): String {
    val user = allUsers[chatUserId] ?: return chatUserId
    return chatUserPrimaryLine(user)
}

fun chatUserShortPrimaryLineById(chatUserId: String, allUsers: Map<String, All>): String {
    val user = allUsers[chatUserId] ?: return chatUserId
    return chatUserShortPrimaryLine(user)
}

fun chatUserHoverLineById(chatUserId: String, allUsers: Map<String, All>): String {
    val user = allUsers[chatUserId] ?: return chatUserId
    return chatUserHoverLine(user)
}

fun shortUserDisplayName(user: All): String {
    val first = user.vorname
    val last = user.nachname
    if (first.isNotBlank() && last.isNotBlank()) {
        return "${first.first().uppercaseChar()}.${last}"
    }
    if (user.name.isNotBlank()) return user.name
    if (user.kuerzel.isNotBlank()) return user.kuerzel
    return userDisplayName(user)
}

fun userInitials(user: All): String {
    if (user.kuerzel.isNotBlank()) return user.kuerzel
    val first = user.vorname.firstOrNull()?.uppercaseChar()?.toString().orEmpty()
    val last = user.nachname.firstOrNull()?.uppercaseChar()?.toString().orEmpty()
    val initials = first + last
    if (initials.isNotBlank()) return initials
    return userDisplayName(user).split(" ").filter { it.isNotBlank() }.take(2).joinToString("") {
        it.first().uppercaseChar().toString()
    }.ifBlank { user.id.take(2).uppercase() }
}

fun userDisplayName(user: All): String {
    val fn = user.vorname
    val ln = user.nachname
    val full = listOf(fn, ln).filter { it.isNotBlank() }.joinToString(" ")
    if (full.isNotBlank()) return full
    if (user.name.isNotBlank()) return user.name
    return user.personalnummer.ifBlank { user.id }
}

@Composable
fun CompanyHeaderRow(
    label: String,
    onClick: () -> Unit
) {
    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(8.px)
            padding(6.px, 0.px)
            cursor("pointer")
        }
        onClick { onClick() }
    }) {
        Img(src = "images/unternehmen.svg", attrs = {
            style {
                width(14.px)
                height(14.px)
                property("filter", "brightness(0) invert(1)")
            }
        })
        Span({
            style {
                fontSize(12.px)
                color(Color.white)
            }
        }) {
            Text(label)
        }
    }
}

@Composable
fun QuickStartRow(
    user: All,
    onClick: () -> Unit
) {
    val name =
        (user.vorname.firstOrNull()?.uppercaseChar()?.toString() ?: "") +
                "." +
                user.nachname

    Div({
        style {
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            gap(8.px)
            padding(6.px, 0.px, 6.px, 14.px)
            cursor("pointer")
        }
        onClick { onClick() }
    }) {
        Img(src = "images/user.svg", attrs = {
            style {
                width(14.px)
                height(14.px)
                property("filter", "brightness(0) invert(1)")
            }
        })
        Span({
            style {
                fontSize(12.px)
                color(Color.white)
            }
        }) {
            Text(name)
        }
    }
}
